import { skyFlightCapabilities } from './106-sky-capabilities.mjs';
import { skyProjectileContact, skyValidatedAimPoint } from './80-sky-aim.mjs';
import { skyLeviathanArmorTargets, skyLeviathanTailContact } from './97-sky-leviathan-armor.mjs';
import {
  createSkyLeviathan,
  stepSkyLeviathan,
  applySkyLeviathanContact,
  skyLeviathanSystems,
  skyLeviathanTargets,
  SKY_LEVIATHAN_POLICY,
} from './93-sky-leviathan.mjs';
import {
  skyLeviathanGeometry,
  skyLeviathanComponentCenters,
  skyLeviathanRouteFrame,
  skyLeviathanInteriorPath,
  skyLeviathanWalls,
  skyLeviathanMachinery,
  skyLeviathanBoxContact,
  skyLeviathanBoxSweep,
  skyLeviathanVestibule,
  skyLeviathanVestibuleBounds,
  SKY_LEVIATHAN_ASSEMBLY,
  skyLeviathanRupture,
  skyLeviathanRuptureSweep,
  skyLeviathanTailBeat,
  skyLeviathanCannons,
} from './95-sky-leviathan-space.mjs';
import {
  skyLeviathanTriangleSweep,
  skyLeviathanSupportHeight,
} from './96-sky-leviathan-contacts.mjs';
import {
  skyFlightFrameFromTangent,
  skyFlightPointToLocal,
  skyFlightPointToWorld,
  skyFlightVectorToWorld,
} from './92-sky-flight-frame.mjs';
import {
  SKY_LEVIATHAN_SPACE as SPACE,
  SKY_LEVIATHAN_FLIGHT,
  SKY_WORLD,
} from './86-sky-world-scale.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';
import { skyEffectiveEquipment, stepSkyBonuses, clearSkyBonuses } from './82-sky-bonuses.mjs';
import { stepSkyOverdrive } from './79-sky-precision.mjs';
import { emitSkyShots, stepSkyProjectiles, skyProjectileAlive } from './81-sky-projectiles.mjs';
import { SKY_TRAVERSAL_TIMING } from './88-sky-encounter-beat.mjs';
import { SKY_LEVIATHAN_JOURNEY } from './105-sky-leviathan-journey.mjs';

const p = (x, y, z) => ({ x, y, z });
const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
const ease = (value) => {
  const t = clamp(value, 0, 1);
  return t * t * t * (10 - 15 * t + 6 * t * t);
};
const blend = (a, b, t) => p(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t);
const position = (s) => p(s.x, s.y, s.pilotZ);
const setPosition = (s, point) => {
  s.x = point.x;
  s.y = point.y;
  s.pilotZ = point.z;
};
const inwardFrame = (hull, point) =>
  skyFlightFrameFromTangent(
    skyFlightPointToWorld(hull, point),
    skyFlightVectorToWorld(hull, p(-point.x, 0, -point.z)),
    skyFlightVectorToWorld(hull, p(point.z, 0, -point.x)),
  );

/** Start a real encounter adapter without changing the campaign manifest.
 * Its rigid hull, actual route and all visible hazard geometry are domain
 * state. The caller owns final rewards/lives/sector transitions. */
export function startSkyLeviathanFlight(s) {
  if (s.leviathan) return s.leviathan;
  const hull = skyFlightFrameFromTangent(
    p(0, SKY_LEVIATHAN_JOURNEY.cloudCenterY - 2, SKY_LEVIATHAN_FLIGHT.hullZ),
    p(0, 0, -1),
  );
  const encounter = createSkyLeviathan({
    id: s.nextId++,
    geometry: skyLeviathanGeometry(),
    frame: hull,
    pilot: position(s),
  });
  const ids = Object.fromEntries(
    encounter.geometry.components.map((part) => [part.id, s.nextId++]),
  );
  s.leviathan = {
    sector: s.sector,
    encounter,
    hullFrame: hull,
    ids,
    wallIds: {},
    phase: 'arrival',
    route: { s: -5, u: 0, v: 0 },
    arrival: position(s),
    walls: [],
    machinery: [],
    components: [],
    dolphins: [],
    hazards: [],
    cue: null,
    volley: -1,
    consumedEvent: 0,
    settled: false,
  };
  s.boss = {
    id: encounter.id,
    kind: 'boss',
    form: 'gildedLeviathan',
    profile: 'gildedLeviathan',
    artVariant: 'gilded-leviathan',
    encounterSector: s.sector,
    name: 'THE GILDED LEVIATHAN',
    collisionEnabled: false,
    hp: 1,
    maxHp: 1,
    controlMode: 'flyby',
    aimWindow: false,
    aimPreparing: false,
    age: 0,
    x: 0,
    y: hull.origin.y,
    z: SKY_LEVIATHAN_FLIGHT.hullZ,
  };
  s.enemies = [];
  s.shots = [];
  s.threats = [];
  s.traversal = null;
  s.flightAimDepth = SPACE.aimPlaneDepth;
  s.aimPoint = null;
  s.locomotion = 'fly';
  s.flightFrame = skyFlightFrameFromTangent(position(s), p(0, 0, -1));
  const systems = skyLeviathanSystems(encounter);
  s.leviathan.walls = skyLeviathanWalls(systems);
  s.leviathan.machinery = skyLeviathanMachinery(0);
  proxies(s, systems);
  Object.assign(s.boss, { systems, hullFrame: hull });
  return s.leviathan;
}

function steering(value, target, axis, limit, speed, dt) {
  const desired = clamp(
    Number.isFinite(target)
      ? target
      : value + (Number.isFinite(axis) ? clamp(axis, -1, 1) : 0) * speed * dt,
    -limit,
    limit,
  );
  const delta = desired - value;
  return value + clamp(delta, -speed * dt, speed * dt);
}
function movePilot(s, input, dt, equipment, systems) {
  const live = s.leviathan,
    e = live.encounter,
    route = live.route;
  const old = { ...route };
  const phase = e.phase;
  let frame;
  if (phase === 'arrival') {
    const target = skyFlightPointToWorld(live.hullFrame, p(0, 0, 43));
    const point = blend(
      live.arrival,
      target,
      ease((e.phaseAge + dt) / SKY_LEVIATHAN_POLICY.arrivalSeconds),
    );
    frame = skyFlightFrameFromTangent(point, p(0, 0, -1));
  } else if (['entry', 'throat', 'chamber', 'breakout'].includes(phase)) {
    const limitU = phase === 'entry' ? 5 : SPACE.interiorLateral;
    const limitV = phase === 'entry' ? 2.2 : SPACE.interiorVertical;
    s.controlBounds = { x: limitU, y: limitV, coordinates: 'flight-local' };
    if (systems.controls) {
      route.u = steering(route.u, input.targetX, input.x, limitU, 3, dt);
      route.v = steering(route.v, input.targetY, input.y, limitV, 2, dt);
    }
    if (phase === 'entry') {
      if (systems.jaw === 1) route.s += dt * 2.8;
      // A missed aperture reoffers from the real forward lane, never enters
      // the interior on a timer or teleports an off-axis pilot through a wall.
      route.s = Math.min(
        route.s,
        skyLeviathanInteriorPath(0).position.z -
          e.geometry.mouth.center.z +
          e.geometry.pilotRadius +
          0.3,
      );
    } else if (phase === 'throat') route.s += dt * 2.8;
    else if (phase === 'chamber') route.s = Math.min(34, route.s + dt * 2.8);
    else if (!e.exited) {
      route.u *= Math.exp(-dt * 3);
      route.v *= Math.exp(-dt * 3);
      if (systems.breach === 1) route.s += dt * 3.5;
    } else
      route.s =
        live.returnS +
        (SPACE.routeLength - live.returnS) *
          ease((e.returnAge + dt) / SKY_TRAVERSAL_TIMING.returnDuration);
    frame = skyLeviathanRouteFrame(live.hullFrame, route.s, route.u, route.v);
    if (phase === 'breakout' && e.exited && systems.woundTurn > 0) {
      const angle = Math.PI * systems.woundTurn;
      frame = skyFlightFrameFromTangent(
        frame.origin,
        skyFlightVectorToWorld(live.hullFrame, p(Math.cos(angle), 0, Math.sin(angle))),
      );
    }
    const physical = [...live.walls, ...live.machinery.flatMap((gate) => gate.boxes)];
    const previous = new Map(
      [...live.previousWalls, ...live.previousMachinery.flatMap((gate) => gate.boxes)].map(
        (box) => [box.id, box],
      ),
    );
    const from = skyFlightPointToLocal(
      live.hullFrame,
      skyLeviathanRouteFrame(live.hullFrame, old.s, old.u, old.v).origin,
    );
    const to = skyFlightPointToLocal(live.hullFrame, frame.origin);
    const requested = { ...route };
    const contact = [
      skyLeviathanRuptureSweep(from, to, systems, e.geometry.pilotRadius),
      ...physical.map((box) =>
        skyLeviathanBoxSweep(from, to, e.geometry.pilotRadius, box, previous.get(box.id) ?? box),
      ),
      ...skyLeviathanVestibule(systems).map((triangle) =>
        skyLeviathanTriangleSweep(from, to, triangle, e.geometry.pilotRadius),
      ),
    ]
      .filter(Boolean)
      .sort((a, b) => a.fraction - b.fraction)[0];
    if (contact) {
      const fraction = Math.max(0, contact.fraction - 1e-5);
      for (const axis of ['s', 'u', 'v'])
        route[axis] = old[axis] + (requested[axis] - old[axis]) * fraction;
      frame = skyLeviathanRouteFrame(live.hullFrame, route.s, route.u, route.v);
      live.blocked = true;
      live.contact = contact;
      // Continue harmless lateral sliding from the actual contact plane. This
      // avoids charging damage while stopping a visible distance before a wall.
      const at = skyFlightPointToLocal(live.hullFrame, frame.origin);
      const slide = skyLeviathanRouteFrame(live.hullFrame, route.s, requested.u, requested.v);
      const end = skyFlightPointToLocal(live.hullFrame, slide.origin);
      if (
        !skyLeviathanRuptureSweep(at, end, systems, e.geometry.pilotRadius) &&
        !physical.some((box) => skyLeviathanBoxSweep(at, end, e.geometry.pilotRadius, box)) &&
        !skyLeviathanVestibule(systems).some((triangle) =>
          skyLeviathanTriangleSweep(at, end, triangle, e.geometry.pilotRadius),
        )
      ) {
        route.u = requested.u;
        route.v = requested.v;
        frame = slide;
      }
    }
  } else if (phase === 'wound') {
    route.u = steering(route.u, input.targetX, input.x, 4, equipment.lateralSpeed, dt);
    route.v = steering(route.v, input.targetY, input.y, 4, 6, dt);
    s.controlBounds = { x: 4, y: 4, coordinates: 'flight-local' };
    const previousAngle = live.orbitAngle ?? 0;
    // Slow along the wound-facing broadside so physical precision shots have
    // a meaningful window; accelerate around the armored rear quarters.
    const facing = Math.max(0, Math.cos(previousAngle)) ** 12;
    live.orbitAngle = previousAngle + dt * (0.14 - 0.122 * facing);
    const angle = live.orbitAngle;
    route.angle = angle;
    const point = p(16 * Math.cos(angle), route.v, 38 * Math.sin(angle));
    frame = inwardFrame(live.hullFrame, point);
    frame.origin = skyFlightPointToWorld(frame, p(route.u, 0, 0));
  } else if (phase === 'perimeter') {
    route.u = steering(route.u, input.targetX, input.x, 18, equipment.lateralSpeed, dt);
    route.v = steering(route.v, input.targetY, input.y, 8, 8, dt);
    s.controlBounds = { x: 18, y: 8, coordinates: 'flight-local' };
    frame = skyFlightFrameFromTangent(
      skyFlightPointToWorld(live.hullFrame, p(route.u, route.v, 43)),
      skyFlightVectorToWorld(live.hullFrame, p(0, 0, -1)),
      live.hullFrame.right,
    );
  } else {
    const end = p(0, skyTerrainHeight(0, 1, s.terrainTravel) + 4.4, 1);
    const returnSeconds = SKY_LEVIATHAN_POLICY.crashDescentSeconds / 2;
    const point =
      phase === 'crash'
        ? blend(live.terminalPilot, end, ease((e.phaseAge - returnSeconds + dt) / returnSeconds))
        : position(s);
    frame = skyFlightFrameFromTangent(point, p(0, 0, -1));
  }
  if (phase === 'throat') {
    const local = skyFlightPointToLocal(live.hullFrame, frame.origin);
    const gate = live.machinery.find((item) =>
      item.boxes.some((box) => skyLeviathanBoxContact(local, e.geometry.pilotRadius, box)),
    );
    if (gate) {
      const clearance = gate.gapHalfWidth - e.geometry.pilotRadius - 0.01;
      const previousU = route.u;
      route.u = clamp(route.u, gate.gapCenter - clearance, gate.gapCenter + clearance);
      live.gatePush = route.u - previousU;
      frame = skyLeviathanRouteFrame(live.hullFrame, route.s, route.u, route.v);
      live.blocked = true;
      live.contact ??= { boxId: gate.id, fraction: 0, point: local };
    }
  }
  s.flightFrame = frame;
  setPosition(s, frame.origin);
  s.locomotion = 'fly';
}

function hullPose(s, dt, systems) {
  const live = s.leviathan,
    e = live.encounter;
  const age = e.age + dt;
  let y = SKY_LEVIATHAN_FLIGHT.cruiseY + Math.sin(age * 0.32) * 0.22,
    z = SKY_LEVIATHAN_FLIGHT.hullZ;
  // The complete physical assembly rises through the real cloud deck during
  // protected arrival. Quintic easing joins the normal cruise pose without a
  // second camera-only whale or a collision/skin mismatch at the handback.
  if (e.phase === 'arrival') {
    const initialY = SKY_LEVIATHAN_JOURNEY.cloudCenterY - 2;
    y = initialY + (y - initialY) * ease((e.phaseAge + dt) / SKY_LEVIATHAN_POLICY.arrivalSeconds);
  }
  const desiredRoll =
    Math.sin(age * 0.4) * 0.012 + (systems.starboardLift - systems.portLift) * 0.12;
  let roll =
    (live.roll ?? desiredRoll) +
    (desiredRoll - (live.roll ?? desiredRoll)) * (1 - Math.exp(-dt * 2));
  if (e.phase === 'crash') {
    const t = systems.crashDescent;
    const settledRoll = SKY_LEVIATHAN_ASSEMBLY.crash.roll;
    const neutralFrame = {
      origin: p(0, 0, z),
      right: p(Math.cos(settledRoll), Math.sin(settledRoll), 0),
      up: p(-Math.sin(settledRoll), Math.cos(settledRoll), 0),
      back: p(0, 0, 1),
    };
    const groundY = skyLeviathanSupportHeight(
      neutralFrame,
      e.geometry.crashProbes,
      s.terrainTravel,
    );
    y = live.crashY + (groundY - live.crashY) * t;
    roll = live.crashRoll + (settledRoll - live.crashRoll) * t;
  } else if (['escaping', 'escaped'].includes(e.phase)) z -= 100 * ease((e.phaseAge + dt) / 4);
  live.roll = roll;
  live.hullFrame = {
    origin: p(0, y, z),
    right: p(Math.cos(roll), Math.sin(roll), 0),
    up: p(-Math.sin(roll), Math.cos(roll), 0),
    back: p(0, 0, 1),
  };
  if (e.phase === 'crash')
    live.hullFrame.origin.y = Math.max(
      live.hullFrame.origin.y,
      skyLeviathanSupportHeight(live.hullFrame, e.geometry.crashProbes, s.terrainTravel),
    );
}
function projectile(s, origin, target, radius = 0.28, speed = 20) {
  const distance = Math.hypot(target.x - origin.x, target.y - origin.y, target.z - origin.z);
  const seconds = Math.max(1.2, distance / speed);
  s.threats.push({
    id: s.nextId++,
    kind: 'threat',
    source: 'leviathan',
    attackKind: 'energy',
    ...origin,
    origin: { ...origin },
    vx: (target.x - origin.x) / seconds,
    vy: (target.y - origin.y) / seconds,
    vz: (target.z - origin.z) / seconds,
    radius,
    age: 0,
  });
}
function hazards(s, dt, hit) {
  const live = s.leviathan,
    e = live.encounter;
  if (!skyLeviathanSystems(e).controls) {
    live.cue = null;
    live.tail = { ...skyLeviathanTailBeat(e), origin: null };
    s.threats = [];
    return;
  }
  const cycle = e.phase === 'chamber' ? 4.8 : 5.6;
  const ordinal = Math.floor(e.phaseAge / cycle),
    clock = e.phaseAge % cycle;
  const cannon = ['perimeter', 'chamber', 'wound'].includes(e.phase);
  const side = ordinal % 2 ? 'port' : 'starboard',
    other = side === 'port' ? 'starboard' : 'port';
  const paired = e.phase === 'perimeter' && skyLeviathanSystems(e).navigation > 0.5;
  if (clock < 1.2 || live.volley !== ordinal || !live.cannonTargets)
    live.cannonTargets = {
      [side]: position(s),
      [other]: { ...position(s), x: s.x + (ordinal % 2 ? -2.2 : 2.2) },
    };
  live.cannons = skyLeviathanCannons(live.hullFrame, live.cannonTargets).map((part) => ({
    ...part,
    charge:
      cannon &&
      part.canFire &&
      e.phase !== 'chamber' &&
      clock < 1.2 &&
      (part.side === side || paired)
        ? clock / 1.2
        : 0,
  }));
  const sources =
    e.phase === 'chamber'
      ? [{ origin: skyFlightPointToWorld(live.hullFrame, p(0, 0, -1.25)), target: position(s) }]
      : live.cannons
          .filter((part) => part.canFire && (part.side === side || paired))
          .map((part) => ({ origin: part.origin, target: live.cannonTargets[part.side] }));
  const origin = sources[0]?.origin;
  live.cue =
    cannon && sources.length > 0 && clock < 1.2
      ? {
          kind: e.phase === 'chamber' ? 'pressure' : 'cannon',
          origin,
          origins: sources.map((source) => source.origin),
          charge: clock / 1.2,
          target: position(s),
          releaseAt: e.age + 1.2 - clock,
        }
      : null;
  if (cannon && clock >= 1.2 && live.volley !== ordinal) {
    live.volley = ordinal;
    for (const source of sources)
      projectile(s, source.origin, source.target, e.phase === 'chamber' ? 0.32 : 0.28);
  }
  for (const bolt of s.threats) {
    const from = p(bolt.x, bolt.y, bolt.z);
    bolt.x += bolt.vx * dt;
    bolt.y += bolt.vy * dt;
    bolt.z += bolt.vz * dt;
    bolt.age += dt;
    if (
      s.enemies.some(
        (target) =>
          target.leviathanWall && skyProjectileContact(target, from, bolt, target.contactFrame),
      )
    ) {
      bolt.age = 20;
      continue;
    }
    const dx = bolt.x - from.x,
      dy = bolt.y - from.y,
      dz = bolt.z - from.z;
    const ratio = clamp(
      ((s.x - from.x) * dx + (s.y - from.y) * dy + (s.pilotZ - from.z) * dz) /
        (dx * dx + dy * dy + dz * dz || 1),
      0,
      1,
    );
    if (
      Math.hypot(
        s.x - from.x - dx * ratio,
        s.y - from.y - dy * ratio,
        s.pilotZ - from.z - dz * ratio,
      ) <
      SKY_WORLD.pilotContactRadius + bolt.radius
    ) {
      hit(s, 'projectile');
      bolt.age = 20;
    }
  }
  s.threats = s.threats.filter((bolt) => bolt.age < 10);
  live.hazards = s.threats;
  const tail = live.armor.find((part) => part.leviathanArmor === 'tail_03');
  live.tail = { ...skyLeviathanTailBeat(e), origin: tail ? p(tail.x, tail.y, tail.z) : null };
  if (live.tail.active && skyLeviathanTailContact(s, live.previousPilot)) hit(s, 'collision');
  if (live.contact && e.phase === 'throat') hit(s, 'collision');
}

function proxies(s, systems) {
  const live = s.leviathan;
  live.systems = systems;
  live.cannons = skyLeviathanCannons(
    live.hullFrame,
    live.cannonTargets ?? { port: position(s), starboard: position(s) },
  ).map((part) => ({ ...part, charge: 0 }));
  live.components = skyLeviathanTargets(live.encounter, live.ids);
  live.dolphins = live.encounter.geometry.components
    .filter((part) => part.kind === 'navigation')
    .map((part) => ({
      id: live.ids[part.id],
      componentId: part.id,
      ...skyFlightPointToWorld(live.hullFrame, live.encounter.centers[part.id]),
      ...live.encounter.parts[part.id],
    }));
  const boxes = [...live.walls, ...live.machinery.flatMap((gate) => gate.boxes)];
  const colliders = boxes.map((box) => ({
    id: (live.wallIds[box.id] ??= s.nextId++),
    kind: 'pillar',
    hp: Infinity,
    value: 0,
    contactShape: 'box',
    halfExtents: { ...box.halfExtents },
    contactFrame: live.hullFrame,
    ...skyFlightPointToWorld(live.hullFrame, box.center),
    radius: Math.hypot(box.halfExtents.x, box.halfExtents.y, box.halfExtents.z),
    obstacleForm: 'leviathan-interior',
    leviathanWall: box.id,
    vulnerable: false,
  }));
  const bounds = skyLeviathanVestibuleBounds(systems);
  const vestibule = {
    id: (live.wallIds.vestibule ??= s.nextId++),
    kind: 'pillar',
    hp: Infinity,
    value: 0,
    vulnerable: false,
    contactShape: 'triangles',
    triangles: skyLeviathanVestibule(systems),
    contactFrame: live.hullFrame,
    ...skyFlightPointToWorld(live.hullFrame, bounds.center),
    radius: bounds.radius,
    obstacleForm: 'leviathan-interior',
    leviathanWall: 'vestibule',
  };
  live.vestibule = skyLeviathanVestibule(systems);
  live.ruptureFragments = skyLeviathanRupture(systems, live.hullFrame);
  const leaves = live.ruptureFragments.map((part) => ({
    id: (live.wallIds[part.id] ??= s.nextId++),
    kind: 'pillar',
    hp: Infinity,
    value: 0,
    ...part.contactFrame.origin,
    contactShape: 'triangles',
    triangles: part.triangles,
    contactFrame: part.contactFrame,
    radius: part.radius,
    vulnerable: false,
    leviathanWall: part.id,
  }));
  live.armor = skyLeviathanArmorTargets(s);
  s.enemies = [...live.components, ...colliders, vestibule, ...leaves, ...live.armor];
}
function consumeEvents(s, callbacks) {
  const live = s.leviathan;
  for (const item of live.encounter.events) {
    if (item.id <= live.consumedEvent) continue;
    live.consumedEvent = item.id;
    if (item.kind === 'component-disabled') {
      const component = live.encounter.geometry.components.find(
        (part) => part.id === item.component,
      );
      const award =
        component.kind === 'navigation' ? 200 : component.kind === 'chamber' ? 600 : 300;
      s.score += award;
      callbacks.earnSalvage(s, 1);
      const point = skyFlightPointToWorld(live.hullFrame, live.encounter.centers[item.component]);
      callbacks.burst(s, point, 'gold', 'kill', {
        scoreAward: award,
        combo: 1,
        source: 'component',
      });
    } else if (item.kind === 'phase-entered')
      callbacks.event(s, 'boss-phase', `LEVIATHAN · ${item.phase.toUpperCase()}`);
  }
}

/** Adapter for the shared run. It advances real movement, visible physical
 * hazards and81 ballistic contacts, then consumes93 component events exactly
 * once. It never auto-destroys objectives because a timer or route finished.
 * Caller invokes only for a playing run; pauses leave every owned value frozen. */
export function stepSkyLeviathanFlight(s, input, dt, callbacks) {
  if (s.status !== 'playing' || !Number.isFinite(dt) || dt <= 0 || dt > 0.1) return s;
  const live = startSkyLeviathanFlight(s),
    e = live.encounter;
  const priorPhase = e.phase,
    // Sample the same endpoint clock used by93 below. Moving colliders must
    // sweep from the previous pose to the visibly rendered current pose.
    systems = skyLeviathanSystems({
      ...e,
      phaseAge: e.phaseAge + dt,
      returnAge: e.exited ? e.returnAge + dt : e.returnAge,
    });
  live.reducedMotion = Boolean(input.reducedMotion);
  s.grace = Math.max(0, s.grace - dt);
  s.pulseTime = Math.max(0, s.pulseTime - dt);
  s.bursts = s.bursts.filter((item) => (item.life -= dt) > 0);
  stepSkyOverdrive(s, dt);
  const equipment = skyEffectiveEquipment(s);
  live.previousPilot = position(s);
  live.previousArmor = live.armor;
  hullPose(s, dt, systems);
  live.previousWalls = live.walls;
  live.previousMachinery = live.machinery;
  live.walls = skyLeviathanWalls(systems);
  live.machinery = skyLeviathanMachinery(e.age + dt);
  live.blocked = false;
  live.contact = null;
  live.gatePush = 0;
  movePilot(s, input, dt, equipment, systems);
  stepSkyLeviathan(e, dt, {
    frame: live.hullFrame,
    pilot: position(s),
    componentCenters: skyLeviathanComponentCenters(e, e.age + dt),
    activeProjectileIds: s.shots.map((shot) => shot.id),
    terrainTravel: s.terrainTravel,
    pilotAlive: s.lives > 0,
  });
  if (e.exited && live.returnS == null) live.returnS = live.route.s;
  if (e.phase !== priorPhase) {
    live.volley = -1;
    s.threats = [];
    s.shots = [];
    if (e.phase === 'entry') live.route.s = -5;
    if (e.phase === 'crash') {
      live.crashY = live.hullFrame.origin.y;
      live.crashRoll = Math.atan2(live.hullFrame.right.y, live.hullFrame.right.x);
      live.terminalPilot = position(s);
    }
    if (e.phase === 'wound') {
      live.route.u = 0;
      live.route.v = 0;
      live.orbitAngle = 0;
    }
  }
  live.phase = e.phase;
  const current = skyLeviathanSystems(e);
  live.walls = skyLeviathanWalls(current);
  proxies(s, current);
  Object.assign(s.boss, live.hullFrame.origin, {
    age: e.age,
    phase: e.phase,
    hp: Object.values(e.parts).reduce((sum, part) => sum + part.hp, 0),
    maxHp: e.geometry.components.reduce((sum, part) => sum + part.hp, 0),
    systems: current,
    hullFrame: live.hullFrame,
    controlMode: current.controls ? (e.phase === 'entry' ? 'steer' : 'aim') : 'flyby',
    aimWindow: current.controls && e.phase !== 'entry',
    aimPreparing: false,
  });
  s.flightAimDepth = SPACE.aimPlaneDepth;
  s.aimPoint = skyValidatedAimPoint(s, input.aimPoint);
  stepSkyBonuses(s, dt);
  if (input.pulse && s.pulse && current.controls) {
    s.pulse = 0;
    s.pulseTime = 0.65;
    s.grace = Math.max(s.grace, 1);
    s.threats = [];
  }
  hazards(s, dt, callbacks.hit);
  if (s.status !== 'playing') {
    clearSkyBonuses(s);
    live.terminalPhase = priorPhase;
    stepSkyLeviathan(e, dt, { frame: live.hullFrame, pilot: position(s), pilotAlive: false });
    live.phase = e.phase;
    return s;
  }
  if (skyFlightCapabilities(s).weapons && s.aimPoint && input.fire !== false)
    emitSkyShots(s, skyEffectiveEquipment(s), dt, input.weaponMuzzles);
  if (current.controls)
    stepSkyProjectiles(s, dt, {
      burst: callbacks.burst,
      destroy: () => {},
      resolveContact: (_state, target, shot, contact, impact) =>
        target.leviathanWall
          ? { handled: true, damage: 0, quality: 'armor' }
          : applySkyLeviathanContact(e, {
              projectileId: shot.id,
              componentId: target.componentId,
              point: contact,
              damage: impact.damage,
            }),
    });
  else s.shots = [];
  s.shots = s.shots.filter(skyProjectileAlive);
  consumeEvents(s, callbacks);
  if (e.outcome === 'defeated' && !live.settled) {
    live.settled = true;
    s.score += 5000;
    s.guardiansDefeated++;
    callbacks.earnSalvage(s, 4);
    s.flightFrame = null;
    s.flightAimDepth = null;
    s.aimPoint = null;
    s.boss = null;
    s.enemies = [];
    s.shots = [];
    s.threats = [];
    callbacks.clearStage(s);
    s.leviathan = null;
  } else if (e.outcome === 'escaped') {
    s.status = 'over';
    clearSkyBonuses(s);
    callbacks.event(s, 'defeat', 'LEVIATHAN ESCAPED');
  }
  return s;
}
